customElements.get('sticky-atc') ||
  customElements.define(
    'sticky-atc',
    class extends HTMLElement {
      constructor() {
        super()
        this.selectors = {
          prodTitle: '.m-sticky-addtocart--title',
          mainImage: '.m-sticky-addtocart--image',
          addToCart: '.m-add-to-cart',
          buyNowBtn: '.m-product-dynamic-checkout',
          variantIdSelect: 'select[name="id"]',
          foxkitBtn: '.foxkit-button',
        }
        this.hasCustomFields = !!document.querySelector('.m-main-product--info .m-product-custom-field')
        this.mainAtcObserver = null
        this.atcObserver = null
        this._onMatchMobile = null
        this._onUnmatchMobile = null
        this._variantChangeUnsubscribe = null
        this._onVariantSelectChange = null
        this._suppressStickySelectHandler = false
        // When the selected variant is sold out, the sticky button becomes a "Notify Me"
        // that opens the existing xflow back-in-stock modal (see setStickyNotifyState).
        this._currentSoldOut = false
        this._stickyNotifyActive = false
      }

      normalizeSizeKey(value) {
        return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase()
      }

      getSizeOptionIndex() {
        const raw = this.dataset.sizeOptionIndex
        const idx = Number(raw)
        return Number.isFinite(idx) && idx > 0 ? idx : 0
      }

      syncMainVariantFromSticky(variant) {
        if (!variant) return
        if (window.__acPdpStickySyncInProgress) return

        window.__acPdpStickySyncInProgress = true
        try {
          if (typeof window.applyAcPdpVariantSelection === 'function') {
            window.applyAcPdpVariantSelection(variant)
            return
          }
          this.syncMainSizeSelectionFromVariant(variant)
        } finally {
          window.__acPdpStickySyncInProgress = false
        }
      }

      syncMainSizeSelectionFromVariant(variant) {
        const optionValues =
          typeof window.getVariantOptionValues === 'function'
            ? window.getVariantOptionValues(variant)
            : Array.isArray(variant?.options)
              ? variant.options
              : []

        const sizeOptionIndex = this.getSizeOptionIndex()
        const sizeValue =
          sizeOptionIndex > 0 ? optionValues[sizeOptionIndex - 1] : optionValues[optionValues.length - 1]
        if (!sizeValue) return

        const sizeButton =
          typeof window.findMainProductSizeVariantButton === 'function'
            ? window.findMainProductSizeVariantButton()
            : null
        if (!sizeButton) return

        const key = this.normalizeSizeKey(sizeValue)
        let targetInput = null
        sizeButton.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (this.normalizeSizeKey(input.value) === key) targetInput = input
        })
        if (!targetInput) return

        if (typeof window.applyAcPdpSizeRadioSelection === 'function') {
          window.applyAcPdpSizeRadioSelection(sizeButton, targetInput)
          return
        }

        targetInput.checked = true
        targetInput.dispatchEvent(new Event('change', { bubbles: true }))
      }

      connectedCallback() {
        this.container = this.closest('.m-sticky-addtocart')
        this.mainProductForm = document.querySelector('.m-product-form--main')
        this.mainProductInfo = document.querySelector('.m-main-product--info')
        this.mainAddToCart = this.mainProductForm?.querySelector('.m-add-to-cart')
        this.mainDynamicCheckout = this.mainProductForm?.querySelector(this.selectors.buyNowBtn)
        this.productId = this.dataset.productId
        this.domNodes = queryDomNodes(this.selectors, this)
        this.variantData = this.getVariantData()
        this.showOnDesktop = this.container?.dataset.showOnDesktop === 'true'
        this.showOnMobile = this.container?.dataset.showOnMobile === 'true'

        if (!this.mainAddToCart) {
          this.container?.style.setProperty('--m-translate-y', '0%')
          return
        }

        this.init()
        this.initVariantSelect()
        this.syncWithMainProductForm()
        this.watchMainAtc()
        this.setStickyAddToCartHeight()

        this._onMatchMobile = () => {
          this.setStickyAddToCartHeight()
          this.refreshStickyVisibility()
        }
        this._onUnmatchMobile = () => {
          this.setStickyAddToCartHeight()
          this.refreshStickyVisibility()
        }
        document.addEventListener('matchMobile', this._onMatchMobile)
        document.addEventListener('unmatchMobile', this._onUnmatchMobile)
      }

      disconnectedCallback() {
        if (this.atcObserver) this.atcObserver.disconnect()
        if (this.mainAtcObserver) this.mainAtcObserver.disconnect()
        if (this._onMatchMobile) document.removeEventListener('matchMobile', this._onMatchMobile)
        if (this._onUnmatchMobile) document.removeEventListener('unmatchMobile', this._onUnmatchMobile)
        if (this._variantChangeUnsubscribe) this._variantChangeUnsubscribe()
        if (this._onVariantSelectChange) {
          this.removeEventListener('change', this._onVariantSelectChange)
        }
        document.documentElement.classList.remove('stick-atc-show')
      }

      getVariantData() {
        return (
          this.variantData ||
          JSON.parse(this.container.querySelector('[type="application/json"]').textContent)
        )
      }

      isStickyEnabledOnCurrentBreakpoint() {
        const isMobile = window.matchMedia('(max-width: 767px)').matches
        return isMobile ? this.showOnMobile : this.showOnDesktop
      }

      setStickyVisible(show) {
        if (!this.isStickyEnabledOnCurrentBreakpoint()) show = false
        this.container.style.setProperty('--m-translate-y', show ? '0%' : '100%')
        document.documentElement.classList.toggle('stick-atc-show', show)
      }

      refreshStickyVisibility() {
        if (!this.atcObserver || !this.mainAddToCart) return
        const rect = this.mainAddToCart.getBoundingClientRect()
        const inView = rect.top < window.innerHeight && rect.bottom > 0
        this.setStickyVisible(!inView)
      }

      initVisibilityObserver() {
        if (!('IntersectionObserver' in window)) {
          this.setStickyVisible(true)
          return
        }

        this.atcObserver = new IntersectionObserver(
          ([entry]) => {
            this.setStickyVisible(!entry.isIntersecting)
          },
          { threshold: 0 }
        )
        this.atcObserver.observe(this.mainAddToCart)
      }

      init() {
        const { prodTitle, mainImage, addToCart, buyNowBtn, foxkitBtn } = this.domNodes

        this.initVisibilityObserver()

        // When in "Notify Me" (sold-out) state, forward the click to the existing xflow
        // back-in-stock button instead of adding to cart / scrolling to the main form.
        // Capture phase ensures this runs before the submit and custom-field handlers.
        const stickyAddBtn = this.querySelector('.sticky-atc-form [name="add"]')
        stickyAddBtn?.addEventListener(
          'click',
          (event) => {
            if (!this._stickyNotifyActive) return
            event.preventDefault()
            event.stopImmediatePropagation()
            this.openXflowNotify()
          },
          true
        )

        prodTitle?.addEventListener('click', () => __scrollToTop())
        mainImage?.addEventListener('click', () => __scrollToTop())

        if (this.hasCustomFields) {
          addToCart?.addEventListener('click', (event) => {
            event.preventDefault()
            event.stopPropagation()
            __scrollToTop(this.mainProductForm, () => this.mainAddToCart.click())
          })

          buyNowBtn?.addEventListener(
            'click',
            (event) => {
              if (validateForm(this.mainProductInfo).length > 0) {
                event.preventDefault()
                event.stopPropagation()
                __scrollToTop(this.mainProductForm, () => this.mainDynamicCheckout.click())
              }
            },
            true
          )

          foxkitBtn?.addEventListener('click', (event) => {
            event.preventDefault()
            __scrollToTop(this.mainProductForm, () => this.mainAddToCart.click())
          })
        }
      }

      initVariantSelect() {
        this._onVariantSelectChange = (event) => {
          const select = event.target.closest(this.selectors.variantIdSelect)
          if (!select) return
          if (this._suppressStickySelectHandler || window.__acPdpSuppressStickySelectChange) return

          const variantId = Number(select.value)
          if (!variantId) {
            this.currentVariant = null
            this.updateButton(true, 'SELECT SIZE')
            return
          }

          this.currentVariant = this.variantData.find((variant) => variant.id === variantId) || null

          if (this.currentVariant) {
            this.syncMainVariantFromSticky(this.currentVariant)
            this._currentSoldOut = !this.currentVariant.available
            this.updateButton(this._currentSoldOut, window.MinimogStrings.soldOut)
            this.setStickyNotifyState(this._currentSoldOut)
          } else {
            this._currentSoldOut = false
            this.updateButton(true, '', true)
            this.setStickyNotifyState(false)
          }
        }

        this.addEventListener('change', this._onVariantSelectChange)
      }

      syncButtonFromMain() {
        const stickyForm = this.querySelector('.sticky-atc-form')
        if (!stickyForm) return

        const stickyBtn = stickyForm.querySelector('[name="add"]')
        const stickyText = stickyBtn?.querySelector('.m-add-to-cart--text')
        const mainBtn = this.mainAddToCart
        const mainText = mainBtn?.querySelector('.m-add-to-cart--text')
        if (!stickyBtn || !mainBtn) return

        if (mainBtn.disabled) {
          stickyBtn.setAttribute('disabled', 'disabled')
        } else {
          stickyBtn.removeAttribute('disabled')
          stickyBtn.classList.remove('disabled')
        }
        stickyBtn.classList.toggle('disabled', mainBtn.classList.contains('disabled'))

        if (mainText && stickyText) {
          stickyText.textContent = mainText.textContent
        }

        // The mirror above copies the main form's disabled "Sold Out" state; re-apply the
        // Notify Me state so the sticky button stays clickable when the variant is sold out.
        if (this._currentSoldOut) this.setStickyNotifyState(true)
      }

      watchMainAtc() {
        if (this.mainAtcObserver) this.mainAtcObserver.disconnect()

        const mainBtn = this.mainAddToCart
        if (!mainBtn) return

        this.syncButtonFromMain()

        this.mainAtcObserver = new MutationObserver(() => {
          this.syncButtonFromMain()
        })
        this.mainAtcObserver.observe(mainBtn, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        })
      }

      updateButton(disabled = true, label, hideText = true) {
        const form = this.querySelector('.sticky-atc-form')
        if (!form) return

        const button = form.querySelector('[name="add"]')
        const textEl = form.querySelector('[name="add"] > .m-add-to-cart--text')
        if (!button) return

        if (disabled) {
          button.setAttribute('disabled', 'disabled')
          if (label && hideText !== false) textEl.textContent = label
        } else {
          button.removeAttribute('disabled')
          button.classList.remove('disabled')
          textEl.textContent = window.MinimogStrings.addToCart
        }
      }

      // Turn the sticky button into (or out of) a clickable "Notify Me" that reuses the
      // xflow back-in-stock modal. Called after every state update so the mirror from the
      // main form (syncButtonFromMain) doesn't leave a dead "Sold Out" button.
      setStickyNotifyState(soldOut) {
        const form = this.querySelector('.sticky-atc-form')
        const button = form?.querySelector('[name="add"]')
        const textEl = button?.querySelector('.m-add-to-cart--text')
        if (!button || !textEl) return

        if (soldOut) {
          this._stickyNotifyActive = true
          button.removeAttribute('disabled')
          button.classList.remove('disabled')
          button.classList.add('m-sticky-notify')
          const xflowLabel = document.querySelector('xflow-bis-button button')?.textContent.trim()
          textEl.textContent = xflowLabel || 'Notify Me'
        } else {
          this._stickyNotifyActive = false
          button.classList.remove('m-sticky-notify')
        }
      }

      openXflowNotify() {
        const xflowBtn = document.querySelector('.m-product-form--wrapper xflow-bis-button button, xflow-bis-button button')
        if (xflowBtn) {
          xflowBtn.click()
          return
        }
        // Fallback: xflow hasn't mounted its button — bring the shopper to the main form.
        if (this.mainProductForm && typeof __scrollToTop === 'function') {
          __scrollToTop(this.mainProductForm)
        }
      }

      syncWithMainProductForm() {
        const variantSelect = this.querySelector(this.selectors.variantIdSelect)

        this._variantChangeUnsubscribe = window.MinimogEvents.subscribe(
          `${this.productId}__VARIANT_CHANGE`,
          (variant) => {
            this._suppressStickySelectHandler = true
            try {
              if (variant && variant.id) {
                if (variantSelect) variantSelect.value = String(variant.id)
                this.currentVariant = variant
                this._currentSoldOut = !variant.available
                this.updateButton(this._currentSoldOut, window.MinimogStrings.soldOut)
              } else if (!window.__acPdpStickySyncInProgress) {
                if (variantSelect) variantSelect.value = ''
                this.currentVariant = null
                this._currentSoldOut = false
                this.updateButton(true, 'SELECT SIZE')
              }
              this.syncButtonFromMain()
              this.setStickyNotifyState(this._currentSoldOut)
            } finally {
              this._suppressStickySelectHandler = false
            }
          }
        )
      }

      setStickyAddToCartHeight() {
        const height = this.container?.offsetHeight || this.offsetHeight
        document.documentElement.style.setProperty('--f-sticky-atc-bar-height', height + 'px')
        window.MinimogSettings.stickyAddToCartHeight = height
      }
    }
  )
